import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
  Box,
  IconButton,
  InputAdornment,
  Typography,
  useTheme,
} from '@mui/material';
import { confirmResetPassword, resetPassword } from 'aws-amplify/auth';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/custom/Button';
import TextField from '../../../components/custom/TextField';
import TextFieldAuth from '../../../components/custom/TextFieldAuth';
import { errorMessages } from '../../../utils/errors/auth-errors'; // Assuming you have mapped Amplify errors here too

interface ResetPasswordFormValues {
  email: string;
  confirmationCode: string;
  newPassword: string;
}

const Reset: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [stage, setStage] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const {
    handleSubmit,
    control,
    setError,
    formState: { errors },
    clearErrors,
    getValues,
  } = useForm<ResetPasswordFormValues>({
    defaultValues: {
      email: '',
      confirmationCode: '',
      newPassword: '',
    },
  });

  const getButtonText = (): string => {
    if (stage === 1) {
      return 'שלח קוד אימות';
    }
    if (stage === 2 || stage === 3) {
      return 'איפוס סיסמה';
    }
    if (stage === 4) {
      return 'חזרה לכניסה';
    }
    return 'Error';
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const onSubmit = async (data: ResetPasswordFormValues) => {
    setIsLoading(true);
    clearErrors();
    setSuccessMessage('');

    if (stage === 1) {
      try {
        await resetPassword({ username: data.email });
        setStage(2);
      } catch (err: any) {
        console.error('Error requesting password reset code:', err);
        const errorCode = err.name || 'default';
        const message = errorMessages[errorCode] || errorMessages.default;
        setError('email', { type: 'manual', message: message });
      } finally {
        setIsLoading(false);
      }
    } else if (stage === 3) {
      const emailValue = getValues('email');
      if (!emailValue) {
        setError('email', { type: 'manual', message: 'Email is required.' });
        setIsLoading(false);
        return;
      }
      try {
        await confirmResetPassword({
          username: emailValue,
          confirmationCode: data.confirmationCode,
          newPassword: data.newPassword,
        });
        setStage(4);
        setSuccessMessage('הסיסמה שונתה בהצלחה');
      } catch (err: any) {
        console.error('Error confirming password reset:', err);
        const errorCode = err.name || 'default';
        const message = errorMessages[errorCode] || errorMessages.default;

        if (
          errorCode === 'CodeMismatchException' ||
          errorCode === 'ExpiredCodeException'
        ) {
          setError('confirmationCode', { type: 'manual', message: message });
        } else if (errorCode === 'InvalidPasswordException') {
          setError('newPassword', { type: 'manual', message: message });
        } else {
          setError('newPassword', { type: 'manual', message: message });
        }
      } finally {
        setIsLoading(false);
      }
    } else if (stage === 4) {
      navigate('/login');
      setIsLoading(false);
    }
  };

  const handleConfirmationCodeChange = (value: string) => {
    clearErrors('confirmationCode');
    if (value.length === 6 && stage === 2) {
      setStage(3);
    }
  };
  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        borderColor: theme.palette.divider,
        p: '25px',

        my: '20px',
        gap: '20px',
        borderRadius: '20px',
        width: '400px',
      }}
      autoComplete="on"
    >
      {stage !== 4 && (
        <Typography
          color={theme.palette.white.main}
          variant="body1"
          fontWeight="fontWeightBold"
          fontSize={32}
        >
          ?שכחת סיסמה
        </Typography>
      )}
      {stage === 1 && (
        <Controller
          name="email"
          control={control}
          defaultValue=""
          rules={{
            required: 'חובה למלא את השדה',
            pattern: {
              value:
                /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
              message: 'כתובת מייל לא חוקית',
            },
          }}
          render={({ field }) => (
            <TextFieldAuth
              {...field}
              id="email"
              label="כתובת מייל"
              fullWidth
              autoComplete="username"
              error={!!errors.email}
              helperText={errors.email ? errors.email.message : ''}
            />
          )}
        />
      )}
      {(stage === 2 || stage === 3) && (
        <Controller
          name="confirmationCode"
          control={control}
          defaultValue=""
          rules={{
            required: 'חובה למלא את השדה',
          }}
          render={({ field }) => (
            <TextField
              {...field}
              id="confirmationCode"
              label="קוד אימות"
              fullWidth
              autoComplete="off"
              onChange={(e) => {
                field.onChange(e);
                handleConfirmationCodeChange(e.target.value);
              }}
              error={!!errors.confirmationCode}
              helperText={
                errors.confirmationCode ? errors.confirmationCode.message : ''
              }
            />
          )}
        />
      )}
      {stage === 3 && (
        <Controller
          name="newPassword"
          control={control}
          defaultValue=""
          rules={{ required: 'חובה למלא את השדה' }}
          render={({ field }) => (
            <TextField
              {...field}
              id="password"
              label="סיסמה חדשה"
              type={showPassword ? 'text' : 'password'}
              fullWidth
              autoComplete="current-password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleClickShowPassword}>
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              error={!!errors.newPassword}
              helperText={errors.newPassword ? errors.newPassword.message : ''}
            />
          )}
        />
      )}
      {stage !== 4 && (
        <Button type="submit" disabled={isLoading || stage === 2}>
          {getButtonText()}
        </Button>
      )}
      {stage === 4 && (
        <Typography
          variant="body1"
          sx={{
            my: '10px',
            textAlign: 'center',
            background: 'linear-gradient(90deg, #BAFF2A, #8BBD22)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
          fontWeight="fontWeightBold"
          fontSize={32}
        >
          הסיסמה שונתה בהצלחה
        </Typography>
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '5px',
        }}
      >
        <Typography
          color={theme.palette.white.main}
          variant="body1"
          fontWeight="fontWeightMedium"
          fontSize={16}
        >
          אין לך חשבון PielaPay?
        </Typography>
        <Typography
          color={theme.palette.white.main}
          variant="body1"
          fontWeight="fontWeightMedium"
          fontSize={16}
          sx={{ cursor: 'pointer', textDecoration: 'underline' }}
          onClick={() => {
            navigate('/signup');
          }}
        >
          צור חשבון
        </Typography>
      </Box>
    </Box>
  );
};

export default Reset;
